import * as yup from 'yup';
import { ObjectSchema } from 'yup';
import {
  OutreachConfigFormFields,
  OutreachConfigSections,
  OutreachTimeFormFields,
  OutreachTriggerTypes,
  SubscriberClearMessageFormFields,
  SubscriberOutreachContentFormFields,
  SupportOutreachClearMessageFormFields,
  SupportOutreachContentFormFields,
  SupportOutreachTriggerFormFields,
} from '../../../types';
import { useTranslation } from 'react-i18next';
import { FormikSupportOutreachTriggerValue } from '../controls/OutreachConfig/support/types';
import { OutreachConfigFormDto } from '../../../../Outreach/hooks/useOutreach';
import { Maybe } from '../../../../../types';
import { useFormikContext } from 'formik';
import { useRecoilValue } from 'recoil';
import {
  outreachConfigEmailAtom,
  outreachConfigPushAtom,
  outreachConfigWebhookAtom,
} from '../../../../../state';
import { FormikOutreachConfigValue } from '../controls/OutreachConfig/FormikOutreachConfigField';

type ValidationSchema = ObjectSchema<any>;

type UseValidationValues = {
  getFormikValidationSchema: () => ValidationSchema;
  getValidationSchemaForSection: (
    sectionName: OutreachConfigSections,
  ) => Maybe<ValidationSchema>;
  isSectionComplete: (sectionName: OutreachConfigSections) => boolean;
};

const useValidation = (): UseValidationValues => {
  const { t } = useTranslation();
  const formikContext = useFormikContext<OutreachConfigFormDto>();
  const configEmail = useRecoilValue(outreachConfigEmailAtom);
  const configWebhook = useRecoilValue(outreachConfigWebhookAtom);
  const configPush = useRecoilValue(outreachConfigPushAtom);

  const supportOutreachContentValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.supportOutreach,
      then: yup.object().shape({
        [SupportOutreachContentFormFields.OutreachRuleName]: yup
          .string()
          .required(t('outreach.form.validation.ruleNameRequired')),
        [SupportOutreachContentFormFields.Recipients]: yup
          .array()
          .min(1, t('outreach.form.validation.recipientsMin1'))
          .of(yup.string())
          .test({
            name: SupportOutreachContentFormFields.Recipients,
            message: t('outreach.form.validation.recipientsMin1'),
            test: (val) => {
              const notEmptyValuesCount =
                val?.filter((v) => v != null && v !== '')?.length || 0;
              return notEmptyValuesCount >= 1;
            },
          })
          .required(t('outreach.form.validation.recipientsMin1')),
        [SupportOutreachContentFormFields.Subject]: yup
          .string()
          .required(t('outreach.form.validation.genericRequired')),
        [SupportOutreachContentFormFields.Introduction]: yup
          .string()
          .required(t('outreach.form.validation.genericRequired')),
      }),
      otherwise: yup.object(),
    });

  const supportOutreachClearMessageShape = {
    [SupportOutreachClearMessageFormFields.Subject]: yup
      .string()
      .required(t('outreach.form.validation.genericRequired')),
    [SupportOutreachClearMessageFormFields.Introduction]: yup
      .string()
      .required(t('outreach.form.validation.genericRequired')),
  };

  const supportOutreachTriggerShapeBasic = {
    [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: yup
      .number()
      .typeError(t('outreach.form.validation.genericNumber'))
      .when(SupportOutreachTriggerFormFields.TriggerType, {
        is: (triggerType: OutreachTriggerTypes) => {
          return [
            OutreachTriggerTypes.MaxPercentage,
            OutreachTriggerTypes.MaxIncreaseRate,
          ].includes(triggerType);
        },
        then: yup
          .number()
          .typeError(t('outreach.form.validation.genericNumber'))
          .moreThan(0, t('outreach.form.validation.genericPercentage'))
          .max(100, t('outreach.form.validation.genericPercentage'))
          .required(t('outreach.form.validation.genericRequired')),
        otherwise: yup
          .number()
          .typeError(t('outreach.form.validation.genericNumber'))
          .when(SupportOutreachTriggerFormFields.TriggerType, {
            is: OutreachTriggerTypes.Time,
            then: yup.number().optional(),
            otherwise: yup
              .number()
              .typeError(t('outreach.form.validation.genericNumber'))
              .when(SupportOutreachTriggerFormFields.TriggerType, {
                is: (triggerType: OutreachTriggerTypes) =>
                  [
                    OutreachTriggerTypes.CSPOutageDetected,
                    OutreachTriggerTypes.MaxValue,
                  ].includes(triggerType),
                then: yup
                  .number()
                  .typeError(t('outreach.form.validation.genericNumber'))
                  .integer(t('outreach.form.validation.genericInteger'))
                  .min(0, t('outreach.form.validation.genericPositive'))
                  .required(t('outreach.form.validation.genericRequired')),
                otherwise: yup
                  .number()
                  .typeError(t('outreach.form.validation.genericNumber'))
                  .moreThan(0, t('outreach.form.validation.genericPositive'))
                  .required(t('outreach.form.validation.genericRequired')),
              }),
          }),
      }),
    [SupportOutreachTriggerFormFields.IssueClearedWhen]: yup
      .number()
      .typeError(t('outreach.form.validation.genericNumber'))
      .when(SupportOutreachTriggerFormFields.TriggerType, {
        is: (triggerType: OutreachTriggerTypes) =>
          [
            OutreachTriggerTypes.MaxPercentage,
            OutreachTriggerTypes.MaxIncreaseRate,
            OutreachTriggerTypes.CSPOutageDetected,
          ].includes(triggerType),
        then: yup
          .number()
          .typeError(t('outreach.form.validation.genericNumber'))
          .required(t('outreach.form.validation.genericRequired'))
          .moreThan(0, t('outreach.form.validation.genericPercentage'))
          .max(100, t('outreach.form.validation.genericPercentage')),
        otherwise: yup
          .number()
          .typeError(t('outreach.form.validation.genericNumber'))
          .when(SupportOutreachTriggerFormFields.TriggerType, {
            is: OutreachTriggerTypes.Time,
            then: yup.number().optional(),
            otherwise: yup
              .number()
              .typeError(t('outreach.form.validation.genericNumber'))
              .integer(t('outreach.form.validation.genericInteger'))
              .moreThan(0, t('outreach.form.validation.genericPositive'))
              .required(t('outreach.form.validation.genericRequired')),
          }),
      }),
    [SupportOutreachTriggerFormFields.TimeRepeatEvery]: yup
      .number()
      .typeError(t('outreach.form.validation.genericNumber'))
      .when(
        [
          SupportOutreachTriggerFormFields.TriggerType,
          SupportOutreachTriggerFormFields.IsOneTimeOnly,
        ],
        {
          is: (triggerType: OutreachTriggerTypes, isOneTimeOnly: boolean) =>
            triggerType === OutreachTriggerTypes.Time && !isOneTimeOnly,
          then: yup
            .number()
            .typeError(t('outreach.form.validation.genericNumber'))
            .integer(t('outreach.form.validation.genericInteger'))
            .moreThan(0, t('outreach.form.validation.genericPositive'))
            .required(t('outreach.form.validation.genericRequired')),
          otherwise: yup
            .number()
            .typeError(t('outreach.form.validation.genericNumber'))
            .optional(),
        },
      ),
  };

  const supportOutreachTriggerShapeEmail = {
    ...supportOutreachTriggerShapeBasic,
    [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreach]: yup
      .number()
      .typeError(t('outreach.form.validation.genericNumber'))
      .integer(t('outreach.form.validation.genericInteger'))
      .moreThan(0, t('outreach.form.validation.genericPositive'))
      .required(t('outreach.form.validation.genericRequired')),
    [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachEvery]: yup
      .number()
      .typeError(t('outreach.form.validation.genericNumber'))
      .integer(t('outreach.form.validation.genericInteger'))
      .moreThan(0, t('outreach.form.validation.genericPositive'))
      .required(t('outreach.form.validation.genericRequired')),
    [SupportOutreachTriggerFormFields.TimeRepeatWeekdays]: yup
      .array()
      .when([SupportOutreachTriggerFormFields.TimeRepeatEveryUnit], {
        is: (timeRepeatEveryUnit: string) => timeRepeatEveryUnit === 'week',
        then: yup.array().min(1),
      }),
  };

  const webhookOutreachTriggerShape = {
    ...supportOutreachTriggerShapeBasic,
    [SupportOutreachTriggerFormFields.TimeRepeatWeekdays]: yup
      .array()
      .when([SupportOutreachTriggerFormFields.TimeRepeatEveryUnit], {
        is: (timeRepeatEveryUnit: string) => timeRepeatEveryUnit === 'week',
        then: yup.array().min(1),
      }),
  };

  const outreachConfigValidationRules = yup.object().shape({
    [OutreachConfigFormFields.SupportOutreach]: yup
      .boolean()
      .test(
        OutreachConfigFormFields.SupportOutreach,
        t('outreach.form.validation.selectOutreachType'),
        function (value) {
          return true;
        },
      ),
    [OutreachConfigFormFields.SubscriberOutreach]: yup
      .boolean()
      .test(
        OutreachConfigFormFields.SubscriberOutreach,
        t('outreach.form.validation.selectOutreachType'),
        function (value) {
          return true;
        },
      ),
    [OutreachConfigFormFields.WebhookOutreach]: yup
      .boolean()
      .test(
        OutreachConfigFormFields.WebhookOutreach,
        t('outreach.form.validation.selectOutreachType'),
        function (value) {
          const selectedWebhook = this.parent.selectedWebhook[0];
          if (Boolean(value)) {
            return Boolean(selectedWebhook);
          }
          return true;
        },
      ),
  });

  const supportOutreachTimeValidationBaseShape = {
    [OutreachTimeFormFields.To]: yup
      .date()
      .min(
        yup.ref(OutreachTimeFormFields.From),
        t('outreach.form.validation.notLessThanFrom'),
      )
      .optional(),
  };

  const supportOutreachTimeValidationShape =
    configEmail && configEmail[0] && configEmail[0].outreachId
      ? supportOutreachTimeValidationBaseShape
      : {
          ...supportOutreachTimeValidationBaseShape,
          [OutreachTimeFormFields.From]: yup
            .date()
            .min(new Date(), t('outreach.form.validation.pastDate'))
            .required(t('outreach.form.validation.timeRequired')),
        };

  const webhookOutreachTimeValidationShape =
    configWebhook && configWebhook[0] && configWebhook[0].outreachId
      ? supportOutreachTimeValidationBaseShape
      : {
          ...supportOutreachTimeValidationBaseShape,
          [OutreachTimeFormFields.From]: yup
            .date()
            .min(new Date(), t('outreach.form.validation.pastDate'))
            .required(t('outreach.form.validation.timeRequired')),
        };

  const subscriberOutreachTimeValidationShape =
    configPush && configPush[0] && configPush[0].outreachId
      ? supportOutreachTimeValidationBaseShape
      : {
          ...supportOutreachTimeValidationBaseShape,
          [OutreachTimeFormFields.From]: yup
            .date()
            .min(new Date(), t('outreach.form.validation.pastDate'))
            .required(t('outreach.form.validation.timeRequired')),
        };

  const supportOutreachTimeValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.supportOutreach,
      then: yup.object().shape(supportOutreachTimeValidationShape),
      otherwise: yup.object(),
    });
  const subscriberOutreachTimeValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.subscriberOutreach,
      then: yup.object().shape(subscriberOutreachTimeValidationShape),
      otherwise: yup.object(),
    });
  const webhookOutreachTimeValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.webhookOutreach,
      then: yup.object().shape(webhookOutreachTimeValidationShape),
      otherwise: yup.object(),
    });

  const supportOutreachTriggerValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.supportOutreach,
      then: yup.object().shape(supportOutreachTriggerShapeEmail),
      otherwise: yup.object(),
    });

  const supportOutreachTriggerWebhookValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.webhookOutreach,
      then: yup.object().shape(webhookOutreachTriggerShape),
      otherwise: yup.object(),
    });

  const subscriberOutreachTriggerValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.subscriberOutreach,
      then: yup.object().shape(supportOutreachTriggerShapeEmail),
      otherwise: yup.object(),
    });
  const supportOutreachClearMessageValidationRules = yup
    .object()
    .when(['supportOutreachTrigger', 'outreachConfig'], {
      is: (
        trigger: FormikSupportOutreachTriggerValue,
        config: FormikOutreachConfigValue,
      ) =>
        Boolean(trigger.sendClearEmailWhenIssueCleared) &&
        config.supportOutreach,
      then: yup.object().shape(supportOutreachClearMessageShape),
      otherwise: yup.object(),
    });

  const subscriberOutreachClearMessageValidationRules = yup
    .object()
    .when(
      [
        OutreachConfigSections.SubscriberOutreachTrigger,
        OutreachConfigSections.Config,
      ],
      {
        is: (
          trigger: FormikSupportOutreachTriggerValue,
          config: FormikOutreachConfigValue,
        ) =>
          Boolean(trigger.sendClearEmailWhenIssueCleared) &&
          config.subscriberOutreach,
        then: yup.object().shape({
          [SubscriberClearMessageFormFields.Subject]: yup
            .string()
            .required(t('outreach.form.validation.genericRequired')),
          [SubscriberClearMessageFormFields.Message]: yup
            .string()
            .required(t('outreach.form.validation.genericRequired')),
        }),
        otherwise: yup.object(),
      },
    );

  const subscriberOutreachContentValidationRules = yup
    .object()
    .when(OutreachConfigSections.Config, {
      is: (config: FormikOutreachConfigValue) => config.subscriberOutreach,
      then: yup.object().shape({
        [SubscriberOutreachContentFormFields.OutreachRuleName]: yup
          .string()
          .required(t('outreach.form.validation.ruleNameRequired')),
        [SubscriberOutreachContentFormFields.UrlLink]: yup
          .string()
          .when([SubscriberOutreachContentFormFields.UrlEnabled], {
            is: (enabled: boolean) => enabled,
            then: yup.string().test({
              name: SubscriberOutreachContentFormFields.UrlLink,
              message: t('outreach.form.validation.urlRequired'),
              test: (value) => typeof value === 'string',
            }),
          }),
        [SubscriberOutreachContentFormFields.Subject]: yup
          .string()
          .required(t('outreach.form.validation.genericRequired')),
        [SubscriberOutreachContentFormFields.Message]: yup
          .string()
          .required(t('outreach.form.validation.genericRequired')),
      }),
      otherwise: yup.object(),
    });

  const shape: Record<OutreachConfigSections, ValidationSchema> = {
    [OutreachConfigSections.Config]: outreachConfigValidationRules,
    //support
    [OutreachConfigSections.SupportOutreachTime]: supportOutreachTimeValidationRules,
    [OutreachConfigSections.SupportOutreachContent]: supportOutreachContentValidationRules,
    [OutreachConfigSections.SupportOutreachTrigger]: supportOutreachTriggerValidationRules,
    [OutreachConfigSections.SupportOutreachTimeWebhook]: webhookOutreachTimeValidationRules,
    [OutreachConfigSections.SupportOutreachTriggerWebhook]: supportOutreachTriggerWebhookValidationRules,
    [OutreachConfigSections.SupportOutreachClearMessage]: supportOutreachClearMessageValidationRules,

    //subscriber
    [OutreachConfigSections.SubscriberOutreachTime]: subscriberOutreachTimeValidationRules,
    [OutreachConfigSections.SubscriberOutreachTrigger]: subscriberOutreachTriggerValidationRules,
    [OutreachConfigSections.SubscriberOutreachContent]: subscriberOutreachContentValidationRules,
    [OutreachConfigSections.SubscriberOutreachClearMessage]: subscriberOutreachClearMessageValidationRules,
  };

  const getFormikValidationSchema = () => {
    return yup.object().shape(shape);
  };

  const getValidationSchemaForSection = (
    sectionName: OutreachConfigSections,
  ): Maybe<ValidationSchema> => {
    return shape[sectionName];
  };

  const isSectionComplete = (sectionName: OutreachConfigSections): boolean => {
    if (!formikContext || !formikContext.values) {
      return false;
    }

    const schema = yup.object().shape(shape);
    try {
      schema.validateSync(formikContext.values, {
        abortEarly: false,
      });
      return true;
    } catch (error) {
      const validationError = error as yup.ValidationError;
      if (!validationError?.inner) {
        return true;
      }
      const sectionErrors = validationError.inner.filter((e) => {
        return e.path?.includes(`${sectionName}.`);
      });
      const hasSectionErrors =
        sectionErrors && Object.keys(sectionErrors).length > 0;
      return !Boolean(hasSectionErrors);
    }
  };

  return {
    getFormikValidationSchema,
    getValidationSchemaForSection,
    isSectionComplete,
  };
};

export default useValidation;
